import React from 'react';
import { cn } from '@/src/infrastructure/lib/utils';
import { RATING_LABELS } from './constants';

/**
 * 1–5 star rating control. Fully keyboard accessible — arrow keys move,
 * Space/Enter selects, focus is preserved.
 */
export function FeedbackRating({
  value,
  onChange,
  readOnly = false,
  size = 'md',
}: {
  value: number | null;
  onChange?: (next: number) => void;
  readOnly?: boolean;
  size?: 'sm' | 'md';
}) {
  const stars = [1, 2, 3, 4, 5];
  const sizeClass = size === 'sm' ? 'w-5 h-5' : 'w-7 h-7';

  return (
    <div role="radiogroup" aria-label="Rate your experience" className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        {stars.map((star) => {
          const active = value !== null && star <= value;
          return (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={value === star}
              aria-label={`${star} out of 5 — ${RATING_LABELS[star]}`}
              disabled={readOnly}
              onClick={() => onChange?.(star)}
              className={cn(
                'transition-transform duration-150 rounded-md outline-none',
                'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                !readOnly && 'hover:scale-110',
                readOnly && 'cursor-default',
              )}
            >
              <svg
                viewBox="0 0 24 24"
                fill={active ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth={1.5}
                className={cn(sizeClass, active ? 'text-amber-500' : 'text-zinc-300')}
              >
                <path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.32.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .32-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
              </svg>
            </button>
          );
        })}
      </div>
      {value !== null && (
        <span className="text-sm text-on-surface-variant">{RATING_LABELS[value]}</span>
      )}
    </div>
  );
}
